export function isProSubscription(plan) {
  return String(plan || '').trim().toLowerCase() === 'pro';
}

export function normalizeSubscriptionPlan(plan) {
  return isProSubscription(plan) ? 'Pro' : 'Starter';
}

export function normalizeWhatsAppNumber(input) {
  if (!input || typeof input !== 'string') return null;

  const trimmed = input.trim();
  if (!trimmed) return null;

  const waMeMatch = trimmed.match(/wa\.me\/(\d+)/i);
  if (waMeMatch) return waMeMatch[1];

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 15) return null;

  return digits;
}

export function validateWhatsAppNumber(input, { required = false } = {}) {
  if (!input || !String(input).trim()) {
    if (required) {
      return { valid: false, message: 'WhatsApp number is required for Pro plan' };
    }
    return { valid: true, number: '' };
  } 

  const number = normalizeWhatsAppNumber(String(input));
  if (!number) {
    return {
      valid: false,
      message: 'Invalid WhatsApp number. Use international format e.g. +962790000000',
    };
  }

  return { valid: true, number };
}

export function getCartTotal(cart) {
  return cart.reduce((sum, item) => {
    const price = parseFloat(item.price) || 0;
    return sum + price * item.quantity;
  }, 0);
}

export function getTaxPercentage(restaurant) {
  if (!restaurant) return 0;
  const raw = restaurant.taxPercentage ?? restaurant.tax ?? 0;
  const value = parseFloat(String(raw).trim().replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value;
}

export function getServicePercentage(restaurant) {
  if (!restaurant) return 0;
  const raw = restaurant.servicePercentage ?? restaurant.service ?? 0;
  const value = parseFloat(String(raw).trim().replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value;
}

export function getCartTaxAmount(cart, taxPercentage) {
  const rate = Number(taxPercentage) || 0;
  if (rate <= 0) return 0;
  return (getCartTotal(cart) * rate) / 100;
}

export function getCartServiceAmount(cart, servicePercentage) {
  const rate = Number(servicePercentage) || 0;
  if (rate <= 0) return 0;
  return (getCartTotal(cart) * rate) / 100;
}

export function getOrderTotals(cart, restaurant) {
  const subtotal = getCartTotal(cart);
  const serviceRate = getServicePercentage(restaurant);
  const taxRate = getTaxPercentage(restaurant);
  const serviceAmount = getCartServiceAmount(cart, serviceRate);
  const taxAmount = getCartTaxAmount(cart, taxRate);
  const grandTotal = subtotal + serviceAmount + taxAmount;

  return { subtotal, serviceRate, serviceAmount, taxRate, taxAmount, grandTotal };
}

export function getCartGrandTotal(cart, restaurant) {
  return getOrderTotals(cart, restaurant).grandTotal;
}

export function buildWhatsAppOrderMessage({
  restaurant,
  cart,
  generalNote,
  customerName,
  orderType,
  deliveryAddress,
  language,
}) {
  const isAr = language === 'ar';
  const restaurantName =
    isAr && restaurant.resturentNameAr
      ? restaurant.resturentNameAr
      : restaurant.resturentName;
  const currency = restaurant.currency ? `${restaurant.currency} ` : '';
  const { subtotal, serviceRate, serviceAmount, taxRate, taxAmount, grandTotal } = getOrderTotals(cart, restaurant);
  const lines = [];

  const orderTypeLabel = orderType === 'delivery'
    ? (isAr ? 'توصيل' : 'Delivery')
    : orderType === 'pickup'
      ? (isAr ? 'استلام' : 'Pickup')
      : '';

  if (isAr) {
    lines.push(`🍽 *طلب جديد - ${restaurantName}*`, '');
    if (customerName?.trim()) {
      lines.push(`👤 *العميل:* ${customerName.trim()}`);
    }
    if (orderTypeLabel) {
      lines.push(`📦 *نوع الطلب:* ${orderTypeLabel}`);
    }
    if (orderType === 'delivery' && deliveryAddress?.trim()) {
      lines.push(`📍 *العنوان:* ${deliveryAddress.trim()}`);
    }
    lines.push('');
    cart.forEach((item) => {
      const name = isAr && item.nameAr ? item.nameAr : item.name;
      const lineTotal = (parseFloat(item.price) || 0) * item.quantity;
      lines.push(`• ${name} × ${item.quantity} - ${currency}${lineTotal.toFixed(2)}`);
      if (item.note?.trim()) {
        lines.push(`  📝 ${item.note.trim()}`);
      }
    });
    lines.push('', `*المجموع الفرعي: ${currency}${subtotal.toFixed(2)}*`);
    if (serviceRate > 0) {
      lines.push(`*رسوم الخدمة (${serviceRate}%): ${currency}${serviceAmount.toFixed(2)}*`);
    }
    if (taxRate > 0) {
      lines.push(`*الضريبة (${taxRate}%): ${currency}${taxAmount.toFixed(2)}*`);
    }
    lines.push(`*الإجمالي: ${currency}${grandTotal.toFixed(2)}*`);
    if (generalNote?.trim()) {
      lines.push('', `💬 ملاحظة العميل: ${generalNote.trim()}`);
    }
  } else {
    lines.push(`🍽 *New Order - ${restaurantName}*`, '');
    if (customerName?.trim()) {
      lines.push(`👤 *Customer:* ${customerName.trim()}`);
    }
    if (orderTypeLabel) {
      lines.push(`📦 *Order type:* ${orderTypeLabel}`);
    }
    if (orderType === 'delivery' && deliveryAddress?.trim()) {
      lines.push(`📍 *Address:* ${deliveryAddress.trim()}`);
    }
    lines.push('');
    cart.forEach((item) => {
      const name = item.name;
      const lineTotal = (parseFloat(item.price) || 0) * item.quantity;
      lines.push(`• ${name} x${item.quantity} - ${currency}${lineTotal.toFixed(2)}`);
      if (item.note?.trim()) {
        lines.push(`  📝 ${item.note.trim()}`);
      }
    });
    lines.push('', `*Subtotal: ${currency}${subtotal.toFixed(2)}*`);
    if (serviceRate > 0) {
      lines.push(`*Service (${serviceRate}%): ${currency}${serviceAmount.toFixed(2)}*`);
    }
    if (taxRate > 0) {
      lines.push(`*Tax (${taxRate}%): ${currency}${taxAmount.toFixed(2)}*`);
    }
    lines.push(`*Total: ${currency}${grandTotal.toFixed(2)}*`);
    if (generalNote?.trim()) {
      lines.push('', `💬 Customer note: ${generalNote.trim()}`);
    }
  }

  return lines.join('\n');
}

export function openWhatsAppOrder({
  restaurant,
  cart,
  generalNote,
  customerName,
  orderType,
  deliveryAddress,
  language,
}) {
  const phone =
    normalizeWhatsAppNumber(restaurant.whatsappNumber) ||
    normalizeWhatsAppNumber(restaurant.whatsappLink);

  if (!phone) {
    return { success: false, message: 'Restaurant WhatsApp number is not configured' };
  }

  if (!cart.length) {
    return { success: false, message: 'Cart is empty' };
  }

  const message = buildWhatsAppOrderMessage({
    restaurant,
    cart,
    generalNote,
    customerName,
    orderType,
    deliveryAddress,
    language,
  });
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
  return { success: true };
}
